import { ParticipantSummaryCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/ParticipantSummaryCard.tsx';
import { AddressItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/AddressesCard/AddressItem.tsx';
import { sortAddressesTaggedFirst } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/AddressesCard/addresses.utils.ts';
import { TaxRecordItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/TaxRecordsCard/TaxRecordItem.tsx';
import {
	getPersonById,
	getPersonTaxInformation,
} from '@dealstack/frontend/app/services/Person.service.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { Flex, Skeleton, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';

interface Props {
	/** Empty for the programme's own account, which has no owning person. */
	personId: string;
}

export const ParticipantDetailsTab = ({ personId }: Props) => {
	const tMipManage = useTranslationMipManage();

	const personQuery = useQuery({
		queryKey: [API_PATHS.PERSON_PERSONID_GET, personId],
		queryFn: ({ signal }) => getPersonById(API_PATHS.PERSON_PERSONID_GET, { personId }, signal),
		enabled: !!personId,
	});

	const taxRecordsQuery = useQuery({
		queryKey: [API_PATHS.PERSON_PERSONID_TAX_GET, personId],
		queryFn: () => getPersonTaxInformation(personId),
		enabled: !!personId,
	});

	if (!personId) {
		return (
			<Flex p="1rem">
				<Text size="sm" c="dark.0">
					{tMipManage('bank_accounts.details.participant.no_owner', {
						defaultValue: 'This account belongs to the programme, not to a person.',
					})}
				</Text>
			</Flex>
		);
	}

	if (personQuery.isPending) {
		return (
			<Flex direction="column" gap="0.5rem" p="1rem">
				<Skeleton height={24} width={160} radius="sm" />
				<Skeleton height={238} radius="0.25rem" />
			</Flex>
		);
	}

	const person = personQuery.data;
	const addresses = sortAddressesTaggedFirst(person?.addresses ?? []);
	const taxRecords = taxRecordsQuery.data ?? [];

	return (
		<Flex direction="column" gap="1rem" p="1rem">
			<Text fz={18} fw={600} c="gray.9">
				{tMipManage('bank_accounts.details.participant.title', {
					defaultValue: 'Participant details',
				})}
			</Text>

			{person ? (
				<ParticipantSummaryCard person={person} />
			) : (
				<Text size="sm" c="dark.0">
					{tMipManage('bank_accounts.details.participant.load_error', {
						defaultValue: 'Participant details could not be loaded.',
					})}
				</Text>
			)}

			{addresses.length > 0 && (
				<Section
					title={tMipManage('bank_accounts.details.participant.addresses', {
						defaultValue: 'Address(es)',
					})}
				>
					{addresses.map((address) => (
						<AddressItem key={address.id} address={address} />
					))}
				</Section>
			)}

			{taxRecords.length > 0 && (
				<Section
					title={tMipManage('bank_accounts.details.participant.tax_records', {
						defaultValue: 'Tax record(s)',
					})}
				>
					{taxRecords.map((taxRecord) => (
						<TaxRecordItem key={taxRecord.id} taxRecord={taxRecord} />
					))}
				</Section>
			)}
		</Flex>
	);
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
	<Flex direction="column" gap="0.25rem">
		<Text fz={12} c="dark.0">
			{title}
		</Text>

		{children}
	</Flex>
);
